import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Check, X, ArrowRight, Sparkles, ExternalLink, RotateCcw } from "lucide-react";

const storeKey = (qid) => `qg-look-closer:${qid}`;
const readStore = (qid) => { try { return JSON.parse(localStorage.getItem(storeKey(qid)) || "{}"); } catch { return {}; } };
const refUrl = (ref) => `https://www.biblegateway.com/passage/?search=${encodeURIComponent(ref)}&version=KJV`;
const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

function Option({ o, state, pct, onPick }) {
  const locked = !!state;
  const isCorrect = locked && o.key === state.correct;
  const isChosen = locked && o.key === state.chosen;
  const wrongPick = isChosen && !isCorrect;
  const tone = !locked
    ? "border-border bg-card hover:border-gold hover:bg-gold/5 active:scale-[0.99]"
    : isCorrect
      ? "border-emerald-500/60 bg-emerald-500/10"
      : wrongPick
        ? "border-destructive/50 bg-destructive/5"
        : "border-border bg-card opacity-70";
  return (
    <button
      type="button"
      disabled={locked}
      onClick={() => onPick(o.key)}
      data-testid={`look-closer-option-${o.key}`}
      aria-pressed={isChosen}
      className={`relative w-full overflow-hidden rounded-2xl border px-4 py-4 text-left transition-all duration-200 sm:px-5 ${tone} disabled:cursor-default`}
    >
      {locked && <span className="absolute inset-y-0 left-0 -z-0 bg-foreground/[0.04] transition-[width] duration-700" style={{ width: `${pct}%` }} />}
      <span className="relative flex items-start gap-3">
        <span className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full font-mono text-xs font-semibold ${
          isCorrect ? "bg-emerald-500 text-white" : wrongPick ? "bg-destructive text-destructive-foreground" : "bg-muted text-muted-foreground"
        }`}>
          {isCorrect ? <Check className="h-4 w-4" /> : wrongPick ? <X className="h-4 w-4" /> : o.key}
        </span>
        <span className="flex-1 text-[15px] leading-snug text-foreground sm:text-base">{o.text}</span>
        {locked && <span className="shrink-0 font-mono text-xs text-muted-foreground" data-testid={`look-closer-pct-${o.key}`}>{pct}%</span>}
      </span>
      {wrongPick && state.feedback && (
        <p className="relative mt-3 pl-10 text-sm leading-relaxed text-muted-foreground" data-testid="look-closer-feedback">{state.feedback}</p>
      )}
    </button>
  );
}

export default function LookCloser({ questionId, onReadOn, nextLabel = "Continue", title = "Look Closer" }) {
  const { data } = useQuery({
    queryKey: ["look-closer", questionId],
    queryFn: async () => (await api.get(`/questions/${questionId}/look-closer`)).data,
  });
  const items = data?.items || [];
  const [index, setIndex] = useState(0);
  const [states, setStates] = useState({});
  const [done, setDone] = useState(false);
  const [round, setRound] = useState(0);
  const stored = useMemo(() => readStore(questionId), [questionId]);
  const orders = useMemo(
    () => Object.fromEntries(items.map((it) => [it.id, shuffle(it.options.map((o) => o.key))])),
    [items, round] // eslint-disable-line react-hooks/exhaustive-deps -- intentional: "round" forces a fresh shuffle on retry
  );

  useEffect(() => {
    setIndex(0); setStates({}); setDone(false);
  }, [questionId]);

  useEffect(() => {
    if (round > 0) return; // after "try again" the reader starts fresh (their one counted answer stays)
    // restore locked items for this browser without counting again
    items.forEach((it) => {
      const key = stored[it.id];
      if (key && !states[it.id]) {
        api.get(`/questions/${questionId}/look-closer/${it.id}/reveal`, { params: { key } })
          .then((r) => setStates((s) => ({ ...s, [it.id]: r.data })))
          .catch(() => {});
      }
    });
  }, [items, stored, questionId]); // eslint-disable-line react-hooks/exhaustive-deps -- intentional: fetch saved results once per question, not on every answer

  if (!items.length) return null;
  const item = items[index];
  const state = states[item.id];

  const pick = async (key) => {
    if (state) return;
    const already = readStore(questionId)[item.id];
    const { data: res } = already
      ? await api.get(`/questions/${questionId}/look-closer/${item.id}/reveal`, { params: { key } }) // retry: never counted twice
      : await api.post(`/questions/${questionId}/look-closer/${item.id}/answer`, { key });
    setStates((s) => ({ ...s, [item.id]: res }));
    if (!already) localStorage.setItem(storeKey(questionId), JSON.stringify({ ...readStore(questionId), [item.id]: key }));
  };
  const retry = () => {
    setStates({}); setIndex(0); setDone(false); setRound((r) => r + 1);
  };
  const ordered = (orders[item.id] || item.options.map((o) => o.key)).map((k) => item.options.find((o) => o.key === k));

  return (
    <section className="qg-rise rounded-2xl border border-border bg-card p-6 sm:p-8" data-testid="look-closer">
      <div className="mb-5 flex items-baseline justify-between gap-3">
        <h2 className="flex items-center gap-2 font-serif text-2xl font-semibold text-foreground">
          <Sparkles className="h-5 w-5 text-gold" /> {title}
        </h2>
        <div className="flex items-center gap-3">
          {Object.keys(states).length > 0 && (
            <button
              type="button"
              onClick={retry}
              data-testid="look-closer-retry"
              className="inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-wider text-muted-foreground transition-colors hover:text-foreground"
            >
              <RotateCcw className="h-3 w-3" /> Try again
            </button>
          )}
          {!done && (
            <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground" data-testid="look-closer-progress">
              {index + 1} of {items.length}
            </span>
          )}
        </div>
      </div>

      {done ? (
        <div className="py-6 text-center" data-testid="look-closer-done">
          <p className="font-serif text-2xl text-foreground sm:text-3xl">You looked closer. Now make it your own.</p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
            <button
              type="button"
              onClick={onReadOn}
              data-testid="look-closer-read-on"
              className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
            >
              {nextLabel} <ArrowRight className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={retry}
              data-testid="look-closer-retry-done"
              className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold text-foreground transition-colors hover:border-gold"
            >
              <RotateCcw className="h-4 w-4" /> Try again
            </button>
          </div>
        </div>
      ) : (
        <div key={item.id} className="qg-rise">
          <p className="font-serif text-xl font-medium leading-snug text-foreground sm:text-2xl" data-testid="look-closer-prompt">{item.prompt}</p>
          <div className="mt-5 space-y-3">
            {ordered.map((o) => (
              <Option key={o.key} o={o} state={state} pct={state?.percentages?.[o.key] ?? 0} onPick={pick} />
            ))}
          </div>
          {state && (
            <div className="qg-rise mt-5 rounded-2xl bg-muted/60 p-5" data-testid="look-closer-explanation">
              <p className="text-[15px] leading-relaxed text-foreground/90">{state.explanation}</p>
              <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                <a
                  href={refUrl(state.reference)}
                  target="_blank"
                  rel="noreferrer"
                  className="no-reftag inline-flex items-center gap-1 font-mono text-xs uppercase tracking-wider text-gold hover:underline"
                  data-testid="look-closer-reference"
                >
                  {state.reference} <ExternalLink className="h-3 w-3" />
                </a>
                <span className="font-mono text-[11px] text-muted-foreground">{state.total} reader{state.total === 1 ? "" : "s"} so far</span>
              </div>
            </div>
          )}
          {state && (
            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={() => (index + 1 < items.length ? setIndex(index + 1) : setDone(true))}
                data-testid="look-closer-next"
                className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-2.5 text-sm font-semibold text-foreground transition-colors hover:border-gold"
              >
                Next <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
